import loadable from '@loadable/component';
import type { CompanyResponse } from '@/hooks/useCompany/types';
import { Button } from '@simplywallst/ui-core';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTracking } from 'react-tracking';
import { LATEST_NEWS_AND_UPDATES_ANCHOR } from '../../../../../../constants/anchors';
import CloseButton from './components/CloseButton';
import {
  CloseButtonWrapper,
  ContentWrapper,
  GraphicWrapper,
  InactiveSummaryWrapper,
} from './styled';
import type { StatusClassificationSummary } from './support';
import { getStatusClassificationSummary } from './support';
type Company = CompanyResponse['data'];
const FloatingNotification = loadable(
  () => import('./components/FloatingNotification')
);

type Props = {
  readonly companyId: string;
  readonly uniqueSymbol: string;
  readonly classificationStatus: Company['classification_status'];
};

export const InactiveSummary = ({
  companyId,
  uniqueSymbol,
  classificationStatus,
}: Props) => {
  const { trackEvent } = useTracking();

  const wrapperRef = useRef<HTMLDivElement>(null);

  const [hidden, setHidden] = useState(false);
  const [notificationWidth, setNotificationWidth] = useState(0);

  const summary = useMemo<StatusClassificationSummary | null>(() => {
    return getStatusClassificationSummary(classificationStatus);
  }, [classificationStatus]);

  useEffect(() => {
    if (wrapperRef.current) {
      const observer = new ResizeObserver(
        debounce((elements) => {
          const [element] = elements;

          if (element && element.borderBoxSize) {
            const [box] = element.borderBoxSize;

            setNotificationWidth(box.inlineSize);
          }
        })
      );

      observer.observe(wrapperRef.current);

      return () => observer.disconnect();
    }
  });

  const Graphic = summary?.graphic;

  if (!summary) {
    return null;
  }

  return (
    <>
      {!hidden && (
        <InactiveSummaryWrapper ref={wrapperRef} data-cy-id="inactive-banner">
          <CloseButtonWrapper>
            <CloseButton onClick={() => setHidden(true)} />
          </CloseButtonWrapper>

          <ContentWrapper>
            <h4>{summary.title}</h4>
            <div>{summary.description}</div>

            <Button
              styleType="loud"
              onClick={() => {
                trackEvent({
                  action: 'click',
                  modifier: 'show_latest_events',
                  companyId,
                  uniqueSymbol,
                });

                window.location.replace(`#${LATEST_NEWS_AND_UPDATES_ANCHOR}`);
              }}
            >
              See Latest Events
            </Button>
          </ContentWrapper>
          {Graphic && (
            <GraphicWrapper>
              <Graphic />
            </GraphicWrapper>
          )}
        </InactiveSummaryWrapper>
      )}
      {notificationWidth > 0 && (
        <FloatingNotification width={notificationWidth}>
          {summary.title}
        </FloatingNotification>
      )}
    </>
  );
};
